import type { JSX } from 'preact';
import { ROUTES } from '../routes';
import { useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { Button, Field, Input, OtpInput, QrCode, Segment, Wordmark } from '@xantory/ui';
import { useLogin, useVerifyMfa, ApiError, type LoginResponse, type MfaGate } from '@xantory/api';
import { useAuthStore } from '@xantory/store';
import { useT, useLocaleStore, type Locale } from '@xantory/i18n';
import { BrandMarkIcon } from '@xantory/icons';
import { styles } from './login.styles';

// D-080: cloud login is a two-step machine — password, then a TOTP code
// (enrolment on first login shows the QR to scan). The gate comes back from
// the password step; verification issues the session.
type Step = {
  kind: 'password';
} | {
  kind: 'mfa';
  gate: MfaGate;
};
export function LoginPage() {
  const t = useT('login');
  const tc = useT('common');
  const tu = useT('ui');
  // R-303: this same screen doubles as "add another account", shown over a live
  // session. In that mode there MUST be a way back — otherwise opening it by
  // mistake strands the operator on a login form with a working session behind it.
  const addingAccount = useAuthStore(s => s.addingAccount);
  const cancelAddAccount = useAuthStore(s => s.cancelAddAccount);
  const locale = useLocaleStore(s => s.locale);
  const setLocale = useLocaleStore(s => s.setLocale);
  const login = useLogin();
  const verify = useVerifyMfa();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>({
    kind: 'password'
  });
  const loginError = !login.isError ? null : login.error instanceof ApiError ? login.error.status === 401 ? t('errorBadCredentials') : login.error.message : t('errorGeneric');

  // The gateway answers 400 for BOTH a mistyped digit and a spent challenge
  // (`core/auth/src/grpc.rs` — "incorrect code" vs "challenge expired or attempts
  // exhausted"), so the status alone cannot tell them apart and the message has
  // to. That distinction is the whole point: a wrong digit means "try again",
  // a spent challenge means "only starting over can work" — and MAX_ATTEMPTS is
  // 5, so inviting a retry into a dead challenge actively burns what is left.
  // (The old code checked for 401, which this endpoint never returns, so every
  // failure fell through to a generic line and the real reason was discarded.)
  const verifyFailure = !verify.isError ? null : verify.error instanceof ApiError ? /expired|exhausted|sign in again/i.test(verify.error.message) ? {
    text: t('mfaChallengeDead'),
    dead: true
  } : /incorrect code/i.test(verify.error.message) ? {
    text: t('mfaBadCode'),
    dead: false
  } :
  // Anything else: show what the server actually said rather than
  // flattening an unknown failure into "try again" (same choice the
  // password step above makes).
  {
    text: verify.error.message || t('errorGeneric'),
    dead: false
  } : {
    text: t('errorGeneric'),
    dead: false
  };
  const onPassword = (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (login.isPending) return;
    login.mutate({
      email,
      password
    }, {
      // A gate ⇒ move to the code step; a token ⇒ the hook already set the
      // session and the Router swaps to the dashboard.
      onSuccess: (data: LoginResponse) => {
        if (!data.mfa) return;
        // A NEW challenge starts clean. The verify mutation's error survives a
        // step change, so without this the previous challenge's failure is
        // still on screen — and since a dead-challenge error replaces Verify
        // with "Back to sign in", a stale one would make the code step
        // permanently unsubmittable. Caught by exercising the real recovery
        // path rather than by reading it.
        verify.reset();
        setStep({
          kind: 'mfa',
          gate: data.mfa
        });
      }
    });
  };
  const onVerify = (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (verify.isPending || step.kind !== 'mfa') return;
    verify.mutate({
      challenge_id: step.gate.challenge_id,
      code: code.trim()
    });
  };
  const backToPassword = () => {
    setStep({
      kind: 'password'
    });
    setCode('');
    setPassword('');
    // Clear both mutations' error state — the operator is starting over, so a
    // previous attempt's message must not follow them back to the form.
    verify.reset();
    login.reset();
  };
  return <div {...stylex.props(styles.auth)}>
      <main {...stylex.props(styles.main)}>
        <div {...stylex.props(styles.glow)} />
        <div {...stylex.props(styles.card)}>
          <div {...stylex.props(styles.brand)}>
            <div {...stylex.props(styles.brandMark)}>
              <BrandMarkIcon size={18} />
            </div>
            <Wordmark />
          </div>

          {step.kind === 'password' ? <>
              <h1 {...stylex.props(styles.h1)}>{t('welcome')}</h1>
              <p {...stylex.props(styles.lead)}>{t('lead')}</p>
              <form {...stylex.props(styles.form)} onSubmit={onPassword}>
                <Field label={tc('email')} htmlFor="login-email">
                  <Input id="login-email" type="email" autoComplete="username" autoFocus required placeholder={t('emailPlaceholder')} value={email} disabled={login.isPending} onInput={e => setEmail((e.currentTarget as HTMLInputElement).value)} />
                </Field>
                <Field label={tc('password')} htmlFor="login-password">
                  <Input id="login-password" type="password" autoComplete="current-password" required placeholder={t('passwordPlaceholder')} value={password} disabled={login.isPending} onInput={e => setPassword((e.currentTarget as HTMLInputElement).value)} />
                </Field>
                {loginError && <p {...stylex.props(styles.error)} role="alert">
                    {loginError}
                  </p>}
                <Button type="submit" block loading={login.isPending}>
                  {login.isPending ? t('signingIn') : t('signIn')}
                </Button>
              </form>
              <p {...stylex.props(styles.footLink)}>
                <a {...stylex.props(styles.link)} href={ROUTES.recover}>
                  {t('forgotPassword')}
                </a>
              </p>
            </> : <>
              <h1 {...stylex.props(styles.h1)}>
                {step.gate.mode === 'enrol' ? t('mfaEnrolTitle') : t('mfaCodeTitle')}
              </h1>
              <p {...stylex.props(styles.lead)}>
                {step.gate.mode === 'enrol' ? t('mfaEnrolLead') : t('mfaCodeLead')}
              </p>
              {step.gate.mode === 'enrol' && step.gate.otpauth_url && <>
                  <div {...stylex.props(styles.qrWrap)}>
                    <QrCode value={step.gate.otpauth_url} size={180} />
                  </div>
                  <div {...stylex.props(styles.manualKey)}>
                    {t('mfaManualKey')}: {extractSecret(step.gate.otpauth_url)}
                  </div>
                </>}
              <form {...stylex.props(styles.form)} onSubmit={onVerify}>
                {/* R-239: six boxes, so entering a code LOOKS like entering a
                    code. Same single-string value the text input had. */}
                <Field label={t('mfaCodeLabel')}>
                  <OtpInput value={code} onChange={setCode} disabled={verify.isPending} invalid={verifyFailure != null} autoFocus ariaLabel={t('mfaCodeLabel')} digitLabel={(n, total) => t('otpDigit', {
                n: String(n),
                total: String(total)
              })} />
                </Field>
                {verifyFailure && <p {...stylex.props(styles.error)} role="alert">
                    {verifyFailure.text}
                  </p>}
                {/* A spent challenge cannot be verified by any code, so Verify
                    stops being the action — starting over is. Swap the control
                    rather than leaving a button that is guaranteed to fail. */}
                {verifyFailure?.dead ? <Button type="button" block onClick={backToPassword}>
                    {t('mfaStartOver')}
                  </Button> : <Button type="submit" block loading={verify.isPending} disabled={code.length < 6}>
                    {verify.isPending ? t('mfaVerifying') : t('mfaVerify')}
                  </Button>}
              </form>
              <p {...stylex.props(styles.footLink)}>
                <a {...stylex.props(styles.link)} href={ROUTES.mfaRecover}>
                  {t('mfaLostDevice')}
                </a>
                {'  ·  '}
                <a {...stylex.props(styles.link)} href="#" onClick={e => (e.preventDefault(), backToPassword())}>
                  {t('backToLogin')}
                </a>
              </p>
            </>}

          {addingAccount && <div {...stylex.props(styles.langSwitch)}>
              <Button variant="ghost" block onClick={cancelAddAccount}>
                {tu('cancelAddAccount')}
              </Button>
            </div>}

          <div {...stylex.props(styles.langSwitch)}>
            <Segment options={[{
            value: 'de',
            label: 'DE'
          }, {
            value: 'en',
            label: 'EN'
          }]} value={locale} onChange={v => setLocale(v as Locale)} accentActive />
          </div>
        </div>
      </main>
    </div>;
}

/** Pull the base32 secret out of an otpauth:// URI for manual entry. */
function extractSecret(url: string): string {
  try {
    return new URL(url).searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
}